import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIconProps} from '@dropbox/dig-icons';
import {ReactNode} from 'react';
import {TileId} from 'views/home/dashboard/dashboardModel';
import {Tile} from 'views/home/dashboard/Tile';

/** Shape of the leading accessory skeleton on each row. */
type RowAccessory = 'avatar' | 'thumbnail' | 'none';

type RowTrailing = 'none' | 'chip';

const TileListSkeletonText = () => (
  <Stack gap="12">
    <Box>
      <Skeleton.Text width={140} />
    </Box>
    <Box>
      <Skeleton.Text size="small" width={60} />
    </Box>
  </Stack>
);

const RowAccessorySkeleton = ({accessory}: {accessory: RowAccessory}) => {
  if (accessory === 'avatar') {
    return <Skeleton.Avatar size="small" />;
  }
  if (accessory === 'thumbnail') {
    // NewsThumbnail clips its inner skeleton via `.image` (overflow + border-radius).
    return (
      <Box borderRadius="Medium" style={{width: 60, height: 40, overflow: 'hidden', flexShrink: 0}}>
        <Skeleton.Box style={{width: '100%', height: '100%'}} />
      </Box>
    );
  }
  return null;
};

/**
 * Generic loading state for the list-style dashboard tiles (News, New arrivals,
 * Calendar, …). Mirrors the row layout — leading accessory + two text
 * lines — so the skeleton lines up with the real content once it loads.
 */
const TileListSkeletonRows = ({
  rows,
  accessory,
  trailing,
}: {
  rows: number;
  accessory: RowAccessory;
  trailing: RowTrailing;
}) => (
  <Stack gap="6">
    {Array.from({length: rows}).map((_, idx) => (
      <Box
        key={`tile-skeleton-${idx}`}
        paddingX="8"
        paddingY="6"
        style={{marginInline: -8, opacity: 1 - idx * 0.06}}
      >
        <Split
          alignY={trailing === 'chip' ? 'center' : 'top'}
          gap={trailing === 'chip' ? '8' : '10'}
        >
          {accessory !== 'none' && (
            <Split.Item style={{flexShrink: 0}}>
              <RowAccessorySkeleton accessory={accessory} />
            </Split.Item>
          )}
          <Split.Item width="fill" style={{minWidth: 0}}>
            <TileListSkeletonText />
          </Split.Item>
          {trailing === 'chip' && (
            <Split.Item style={{flexShrink: 0}}>
              <Skeleton.Box style={{width: 64, height: 20}} borderRadius="Medium" />
            </Split.Item>
          )}
        </Split>
      </Box>
    ))}
  </Stack>
);

export const TileListSkeleton = ({
  tileId,
  rows = 3,
  accessory = 'avatar',
  trailing = 'none',
  label,
  icon,
  bare,
}: {
  tileId?: TileId;
  rows?: number;
  accessory?: RowAccessory;
  /** Optional trailing chip placeholder (Jira, workstreams, timezones). */
  trailing?: RowTrailing;
  label?: ReactNode;
  icon?: UIIconProps['src'] | false;
  /** Render only the row placeholders (parent supplies the Tile chrome). */
  bare?: boolean;
}) => {
  const rowsNode = <TileListSkeletonRows rows={rows} accessory={accessory} trailing={trailing} />;

  if (bare) {
    return rowsNode;
  }

  return (
    <Tile tileId={tileId} label={label} icon={icon}>
      {rowsNode}
    </Tile>
  );
};
